/**
 * StudioShell — shared presentational types (§5f).
 *
 * The shell is the 8-stage spine + 3-zone layout from the redesign
 * (docs/TIMETABLE_STUDIO_REBUILD/prototype-app.dc.html). These types are the thin
 * contract between the orchestrator (ResearchStudio) and the chrome
 * (StudioShell / LiveRail); the heavy engine state stays in the orchestrator.
 */

/** The seven stages, in spine order. Design and Compare are ONE stage —
 *  Build — because designing a timetable and seeing it against today is one
 *  act, not two (Chris, 2026-08-24). */
export type StageKey =
  | 'brief' | 'cost' | 'calendar' | 'baseline'
  | 'build' | 'explore' | 'results'

export interface StageDef {
  key: StageKey
  /** 1-based number shown above the label. */
  n: number
  label: string
  /**
   * Whether this stage's own gate is satisfied (MASTER_PLAN §6.1 — no step may
   * be skipped). A completed, non-active stage shows a ✓ instead of its number.
   */
  complete: boolean
}

/** Spine order (Chris, 2026-10-28): the running calendar is stage 2, renamed
 *  Timetable, with the Baseline merged into it as a sub-tab — the key
 *  'baseline' survives only so sessions saved on it still open (they land on
 *  Timetable → Baseline). */
export const STAGE_DEFS: { key: StageKey; label: string }[] = [
  { key: 'brief', label: 'Scope' },
  { key: 'calendar', label: 'Timetable' },
  { key: 'cost', label: 'Cost model' },
  { key: 'build', label: 'Modelling' },
  { key: 'explore', label: 'Explore' },
  { key: 'results', label: 'Results' },
]

export const STAGE_TITLES: Record<StageKey, string> = {
  brief: 'Scope',
  cost: 'Cost model',
  calendar: 'Timetable',
  baseline: 'Baseline timetable',
  build: 'Modelling',
  explore: 'Explore with Claude',
  results: 'Results',
}

export const STAGE_BLURBS: Record<StageKey, string> = {
  brief: 'The question, scope, ownership and the honesty rules this session runs under. Everything downstream is measured against this frame.',
  cost: 'The full running-cost model. A train is a locomotive plus a set of carriages, each costed separately; steam and diesel differ in both running and long-term overhaul cost; infrastructure is costed per mile between each station.',
  calendar: 'Which days actually run, by day-type. Save the pattern as a running timetable set so the season roll-up uses the real operating calendar, not an assumed day-count.',
  baseline: 'The working timetable that “current” means, plus the line geography that drives per-mile costing.',
  build: 'The modelling, month by month: pick a month to read its designed working turns, diagrams and Weekly Service Schedule against what the railway runs today. The settings the model runs under live on the Scope stage; ★ Build Model at the end of the stage bar builds the whole season.',
  explore: 'Talk to Claude. It proposes candidate configurations, this browser scores each with the production engine, and it pauses at visible checkpoints so you can steer or stop.',
  results: 'The board-ready case, in the same sections the exported paper prints. One document serves the board and the planning team.',
}

/** One tile in the Live Results rail's KPI grid. */
export interface RailKpi {
  label: string
  value: string
  sub?: string
  tone?: 'good' | 'warn' | 'bad'
}

/** One row in the constraint-status list (rail + Results ops tab). */
export interface RailConstraint {
  label: string
  detail: string
  kind: 'ok' | 'warn' | 'bad'
}
